import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layers, Plus, Edit2, Trash2, AlertCircle } from "react-feather";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";

import TableContainer from "../../BaseComponents/BaseTable";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseModal from "../../BaseComponents/BaseModal";
import BaseTooltip from "../../BaseComponents/BaseTooltip";

import {
  listOfOrganizationApi,
  addOrganizationApi,
  editOrganizationApi,
  deleteOrganizationApi,
  viewOrganizationApi,
} from "../../Api/OrganizationApi";

import { checkStatusCodeSuccess, handleArrayResponse } from "../../Constant/common";
import { Action, Search, notFound } from "../../Constant";
import {
  OrganizationLabel,
  overtimePolicyTypeOptions,
  OVERTIME_POLICY_TYPE,
  payrollTypeOptions,
} from "../../Constant/Organization/organization";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import { validationMessages } from "../../Constant/validation";
import { DeleteMessage } from "../../Constant/Center";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { truncateText } from "../../Constant/Lead";
import { useTableQueryParams } from "../../utils/tableUtils";

/* ─── helpers ─────────────────────────────────────────────── */
const TipCell = (val, max = 40, color = "#6366f1") => {
  const t = truncateText(val, max);
  return val && val.length > max ? (
    <BaseTooltip label={val} position="top" maxWidth={300} color={color}>
      <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>{t}</span>
    </BaseTooltip>
  ) : (
    <span>{val || "—"}</span>
  );
};

/* ─── component ───────────────────────────────────────────── */
const Organization = () => {
  useEffect(() => {
    document.title = OrganizationLabel.Title;
  }, []);

  const { setLoading } = usePageLoader();

  /* ── table params state ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5 });

  /* ── list state ── */
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);

  /* ── modal / form state ── */
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);

  /* ── fetch list ── */
  const fetchData = useCallback(() => {
    setLoading(true);
    setIsFetching(true);
    listOfOrganizationApi({
      search: searchValue || "",
      order: [columnName, sortOrder],
      pageSize: customPageSize,
      pageNumber: currentPage,
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setData(resp?.data?.listOfOrganizations || []);
          setTotalPages(resp?.data?.totalPages || 1);
          setTotalRecords(resp?.data?.totalRecordsCount || 0);
          setTotalNumberOfRows(resp?.data?.numberOfRows || 0);
        } else {
          setData([]);
          setTotalRecords(0);
          setTotalPages(1);
          setTotalNumberOfRows(0);
        }
      })
      .catch(() => {
        setData([]);
        setTotalRecords(0);
        setTotalPages(1);
        setTotalNumberOfRows(0);
      })
      .finally(() => {
        setLoading(false);
        setIsFetching(false);
      });
  }, [searchValue, columnName, sortOrder, customPageSize, currentPage, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── open add ── */
  const openAdd = () => {
    formik.resetForm();
    setEditId(null);
    setEditData(null);
    setShowFormModal(true);
  };

  /* ── open edit ── */
  const openEdit = useCallback(
    (id) => {
      setLoading(true);
      viewOrganizationApi(id)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            setEditData(resp?.data?.organization || null);
            setEditId(id);
            setShowFormModal(true);
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => setLoading(false));
    },
    [setLoading],
  );

  /* ── close form modal ── */
  const closeFormModal = () => {
    if (btnLoader) return;
    formik.resetForm();
    setEditId(null);
    setEditData(null);
    setShowFormModal(false);
  };

  /* ── delete ── */
  const deleteOrganization = () => {
    setDeleteLoader(true);
    deleteOrganizationApi(deleteId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp?.message);
          resetToFirstPage();
          fetchData();
          setShowDeleteModal(false);
          setDeleteId(null);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message))
      .finally(() => setDeleteLoader(false));
  };

  /* ── formik ── */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      organization_name: editId ? editData?.organization_name || "" : "",
      description: editId ? editData?.description || "" : "",
      overtime_policy_type: editId
        ? editData?.overtime_policy_type || OVERTIME_POLICY_TYPE.PAYMENT
        : OVERTIME_POLICY_TYPE.PAYMENT,
      is_payroll: editId
        ? editData?.is_payroll !== undefined && editData?.is_payroll !== null
          ? editData?.is_payroll
          : true
        : true,
    },
    validationSchema: yup.object({
      organization_name: yup.string().required(validationMessages.required(OrganizationLabel.Name)),
      description: yup
        .string()
        .required(validationMessages.required(OrganizationLabel.Description)),
      overtime_policy_type: yup
        .string()
        .required(validationMessages.required(OrganizationLabel.OvertimePolicyType)),
      is_payroll: yup
        .boolean()
        .required(validationMessages.required(OrganizationLabel.PayrollType)),
    }),
    onSubmit: (values, { resetForm }) => {
      setBtnLoader(true);
      const payload = {
        organization_name: values.organization_name,
        description: values.description,
        overtime_policy_type: values.overtime_policy_type,
        is_payroll: values.is_payroll,
      };
      const apiCall = editId ? editOrganizationApi(editId, payload) : addOrganizationApi(payload);

      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message);
            resetToFirstPage();
            fetchData();
            resetForm();
            setShowFormModal(false);
            setEditId(null);
            setEditData(null);
          } else {
            setData([]);
            // Removed toast for not found scenario to keep UI consistent
          }
        })
        .catch((err) => handleArrayResponse(err))
        .finally(() => setBtnLoader(false));
    },
  });

  /* ── columns ── */
  const columns = useMemo(
    () => [
      {
        header: "SR. NO.",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell) => (
          <span style={{ color: "var(--text-muted)", fontSize: "0.8125rem" }}>
            {(currentPage - 1) * customPageSize + cell.row.index + 1}
          </span>
        ),
      },
      {
        header: OrganizationLabel.Name,
        accessorKey: "organization_name",
        enableColumnFilter: false,
        cell: (c) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                flexShrink: 0,
                background: "rgba(99,102,241,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Layers size={14} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
            </div>
            {TipCell(c.row.original?.organization_name, 36, "#6366f1")}
          </div>
        ),
      },
      {
        header: OrganizationLabel.Description,
        accessorKey: "description",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.description, 50, "#8b5cf6"),
      },
      {
        header: Action,
        accessorKey: "action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rec = row.original;
          return (
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            >
              <BaseTooltip label="Edit Organization" position="top" color="#f59e0b">
                <button
                  type="button"
                  onClick={() => openEdit(rec.id)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    background: "rgba(245,158,11,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(245,158,11,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(245,158,11,0.12)";
                  }}
                  aria-label="Edit Organization"
                >
                  <Edit2 size={14} strokeWidth={2} style={{ color: "#f59e0b" }} />
                </button>
              </BaseTooltip>
              <BaseTooltip label="Delete Organization" position="top" color="#f43f5e">
                <button
                  type="button"
                  onClick={() => {
                    setDeleteId(rec.id);
                    setShowDeleteModal(true);
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    background: "rgba(244,63,94,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(244,63,94,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(244,63,94,0.12)";
                  }}
                  aria-label="Delete Organization"
                >
                  <Trash2 size={14} strokeWidth={2} style={{ color: "#f43f5e" }} />
                </button>
              </BaseTooltip>
            </div>
          );
        },
      },
    ],
    [openEdit, currentPage, customPageSize],
  );

  /* ── render ── */
  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Page header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Layers size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {OrganizationLabel.Title}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0
                ? `${totalRecords} organization${totalRecords !== 1 ? "s" : ""}`
                : OrganizationLabel.Remark}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={openAdd}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> {OrganizationLabel.AddOrganization}
        </BaseButton>
      </div>

      {/* Table */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {data.length > 0 || searchValue ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={handlePageChange}
            currentPage={currentPage}
            fetchData={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            columns={columns}
            data={data}
            isGlobalFilter={true}
            shouldLineBreak={false}
            manualFiltering={true}
            manualPagination={true}
            onSearch={handleSearchChange}
            fetchSortingData={handleSortChange}
            SearchPlaceholder={Search}
            tableClass="table table-bordered text-center"
          />
        ) : (
          !isFetching && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "60px 20px",
                gap: 12,
              }}
            >
              <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
            </div>
          )
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <BaseModal
        isOpen={showFormModal}
        toggler={closeFormModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editId ? (
                <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              ) : (
                <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
              )}
            </div>
            <span>
              {editId ? OrganizationLabel.EditOrganization : OrganizationLabel.AddOrganization}
            </span>
          </div>
        }
        submitText={editId ? "Update" : "Submit"}
        disabled={btnLoader}
        loader={btnLoader}
        submit={formik.handleSubmit}
      >
        <form onSubmit={formik.handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <BaseInput
              label={OrganizationLabel.Name}
              name="organization_name"
              type="text"
              placeholder={PlaceHolderFormat(OrganizationLabel.Name)}
              value={formik.values.organization_name}
              touched={formik.touched.organization_name}
              error={formik.errors.organization_name}
              handleBlur={formik.handleBlur}
              handleChange={formik.handleChange}
              required
            />
            <BaseInput
              label={OrganizationLabel.Description}
              name="description"
              type="textarea"
              placeholder={PlaceHolderFormat(OrganizationLabel.Description)}
              value={formik.values.description}
              touched={formik.touched.description}
              error={formik.errors.description}
              handleBlur={formik.handleBlur}
              handleChange={formik.handleChange}
              required
            />

            {/* ── Overtime Policy Type ── */}
            <div className="mb-3">
              <label
                className="form-label"
                style={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "var(--text-primary)",
                  marginBottom: 10,
                  display: "block",
                }}
              >
                {OrganizationLabel.OvertimePolicyType}
                <span className="text-danger ms-1">*</span>
              </label>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  lineHeight: 1.5,
                }}
              >
                {OrganizationLabel.OvertimePolicyTypeDesc}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {overtimePolicyTypeOptions.map((opt) => {
                  const active = formik.values.overtime_policy_type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => formik.setFieldValue("overtime_policy_type", opt.value)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "14px 16px",
                        borderRadius: 14,
                        textAlign: "left",
                        width: "100%",
                        border: `1.5px solid ${active ? opt.border : "var(--glass-border)"}`,
                        background: active ? opt.bg : "var(--icon-btn-bg)",
                        cursor: "pointer",
                        transition: "all 0.18s",
                        boxShadow: active ? `0 4px 16px ${opt.color}18` : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.borderColor = opt.border;
                          e.currentTarget.style.background = opt.bg;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.borderColor = "var(--glass-border)";
                          e.currentTarget.style.background = "var(--icon-btn-bg)";
                        }
                      }}
                    >
                      {/* Icon bubble */}
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          flexShrink: 0,
                          background: active ? opt.bg : "var(--glass-bg)",
                          border: `1px solid ${active ? opt.border : "var(--glass-border)"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.18s",
                        }}
                      >
                        <opt.Icon
                          size={20}
                          strokeWidth={1.75}
                          style={{
                            color: active ? opt.color : "var(--text-muted)",
                            transition: "color 0.18s",
                          }}
                        />
                      </div>

                      {/* Text */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "0.9375rem",
                            color: active ? opt.color : "var(--text-primary)",
                            marginBottom: 3,
                            transition: "color 0.18s",
                          }}
                        >
                          {opt.label}
                        </div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--text-muted)",
                            lineHeight: 1.5,
                          }}
                        >
                          {opt.description}
                        </div>
                      </div>

                      {/* Radio indicator */}
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          flexShrink: 0,
                          border: `2px solid ${active ? opt.color : "var(--glass-border)"}`,
                          background: active ? opt.color : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.18s",
                          boxShadow: active ? `0 0 0 3px ${opt.color}22` : "none",
                        }}
                      >
                        {active && (
                          <div
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: "#fff",
                            }}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {formik.touched.overtime_policy_type && formik.errors.overtime_policy_type && (
                <div className="invalid-feedback d-block" style={{ marginTop: 6 }}>
                  {formik.errors.overtime_policy_type}
                </div>
              )}
            </div>

            {/* ── Payroll System Type ── */}
            <div className="mb-3">
              <label
                className="form-label"
                style={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "var(--text-primary)",
                  marginBottom: 10,
                  display: "block",
                }}
              >
                {OrganizationLabel.PayrollType}
                <span className="text-danger ms-1">*</span>
              </label>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  lineHeight: 1.5,
                }}
              >
                {OrganizationLabel.PayrollTypeDesc}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {payrollTypeOptions.map((opt) => {
                  const active = formik.values.is_payroll === opt.value;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => formik.setFieldValue("is_payroll", opt.value)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "14px 16px",
                        borderRadius: 14,
                        textAlign: "left",
                        width: "100%",
                        border: `1.5px solid ${active ? opt.border : "var(--glass-border)"}`,
                        background: active ? opt.bg : "var(--icon-btn-bg)",
                        cursor: "pointer",
                        transition: "all 0.18s",
                        boxShadow: active ? `0 4px 16px ${opt.color}18` : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.borderColor = opt.border;
                          e.currentTarget.style.background = opt.bg;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.borderColor = "var(--glass-border)";
                          e.currentTarget.style.background = "var(--icon-btn-bg)";
                        }
                      }}
                    >
                      {/* Icon bubble */}
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          flexShrink: 0,
                          background: active ? opt.bg : "var(--glass-bg)",
                          border: `1px solid ${active ? opt.border : "var(--glass-border)"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.18s",
                        }}
                      >
                        <opt.Icon
                          size={20}
                          strokeWidth={1.75}
                          style={{
                            color: active ? opt.color : "var(--text-muted)",
                            transition: "color 0.18s",
                          }}
                        />
                      </div>

                      {/* Text */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "0.9375rem",
                            color: active ? opt.color : "var(--text-primary)",
                            marginBottom: 3,
                            transition: "color 0.18s",
                          }}
                        >
                          {opt.label}
                        </div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--text-muted)",
                            lineHeight: 1.5,
                          }}
                        >
                          {opt.description}
                        </div>
                      </div>

                      {/* Radio indicator */}
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          flexShrink: 0,
                          border: `2px solid ${active ? opt.color : "var(--glass-border)"}`,
                          background: active ? opt.color : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.18s",
                          boxShadow: active ? `0 0 0 3px ${opt.color}22` : "none",
                        }}
                      >
                        {active && (
                          <div
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: "#fff",
                            }}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {formik.touched.is_payroll && formik.errors.is_payroll && (
                <div className="invalid-feedback d-block" style={{ marginTop: 6 }}>
                  {formik.errors.is_payroll}
                </div>
              )}
            </div>
          </div>
        </form>
      </BaseModal>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={showDeleteModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(244,63,94,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Trash2 size={14} strokeWidth={2} style={{ color: "#f43f5e" }} />
            </div>
            <span>{OrganizationLabel.DeleteOrganization}</span>
          </div>
        }
        toggler={() => {
          if (!deleteLoader) {
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        submit={deleteOrganization}
        submitText="Delete"
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteMessage}
          </p>
        </div>
      </BaseModal>
    </div>
  );
};

export default Organization;
